import styled, { css } from 'styled-components';
import { AVideoAspectRatio } from './AVideo.types';
import { toRem, baseVariables } from '../../styles';

export const getAspectRatioValue = (ratio: AVideoAspectRatio): string => ratio.replace(':', ' / ');

interface BrightcoveWrapperProps {
  $aspectRatio?: AVideoAspectRatio;
  $visible?: boolean;
}

/**
 * Wrapper for the Brightcove player.
 *
 * When $aspectRatio is provided, CSS aspect-ratio constrains the container
 * and the inner .video-js is absolutely positioned to fill it.
 * Brightcove's CDN stylesheet injects a hardcoded padding-top rule that
 * overrides Video.js responsive sizing, so !important on the inner element
 * is required to let the CSS wrapper win.
 */
export const BrightcoveWrapper = styled.div<BrightcoveWrapperProps>`
  inline-size: 100%;
  position: relative;
  visibility: ${({ $visible = true }) => ($visible ? 'visible' : 'hidden')};

  /* Atom-scoped control-bar tokens. The fallback value IS the real value until
     a global token is created. */
  --t-video-control-fg: ${baseVariables.color['base20']};
  --t-video-control-fg-on-light: ${baseVariables.color['base10']};

  ${({ $aspectRatio }) =>
    $aspectRatio &&
    css`
      aspect-ratio: ${getAspectRatioValue($aspectRatio)};

      .video-js {
        position: absolute;
        inset: 0;
        inline-size: 100%;
        /* Video.js fluid mode sets \`height: 0\` and Brightcove's CDN sets
           \`padding-top: 56.25%\` — both at equal specificity to this rule and
           later in source order, so !important is required to let the
           aspect-ratio wrapper size the player. */
        block-size: 100% !important;
        padding-block-start: 0 !important;
      }
    `}

  .video-js {
    .vjs-big-play-button {
      display: none;
    }

    /* Override marriot.global.css \`[class*="icon-"]::before { font-family: MiIcons }\` */
    .vjs-icon-placeholder::before,
    [class*='vjs-icon-']::before,
    .vjs-button > .vjs-icon-placeholder::before {
      font-family: VideoJS;
    }

    .vjs-control-bar {
      position: absolute;
      z-index: var(--avideo-control-bar-z-index, 9);
      inline-size: 100%;
      inset-inline: 0;
      bottom: 0;
      overflow: visible;
      color: var(--t-video-control-fg, ${baseVariables.color['base20']});
      font-size: ${baseVariables.font['fontXs']};

      /* Size only the plain icon-button controls. Excluded:
         - .vjs-progress-control: must flex-grow to fill the bar (see luxury players).
         - .vjs-volume-panel: Video.js expands it to 10em on hover; a fixed width
           here traps the slider inside a 48px box so it overflows onto the time
           display. Let the panel size itself. */
      .vjs-control:not(.vjs-progress-control):not(.vjs-volume-panel) {
        inline-size: ${toRem(38)};
        flex-shrink: 0;

        @media ${baseVariables.mediaQuery.sm} {
          inline-size: ${toRem(48)};
        }
      }

      .vjs-volume-panel,
      .vjs-quality-menu-wrapper {
        flex-shrink: 0;
      }
    }

    .vjs-time-control {
      display: flex;
      inline-size: auto;
      flex-shrink: 0;
      white-space: nowrap;
    }

    .vjs-volume-level,
    .vjs-play-progress,
    .vjs-menu .vjs-menu-item.vjs-selected {
      background-color: var(--t-video-control-fg, ${baseVariables.color['base20']});
    }

    .vjs-menu .vjs-menu-item.vjs-selected {
      color: var(--t-video-control-fg-on-light, ${baseVariables.color['base10']});
    }

    /* The progress scrubber must always fill left-to-right regardless of
       document direction. Scope the guard to the fill track only (not the
       whole .vjs-progress-control) so the control bar still mirrors in RTL.
       stylis-plugin-rtl@1.1.0 strips plain noflip comments; the bang variant
       below survives. */
    /*! @noflip */
    .vjs-progress-holder,
    .vjs-play-progress,
    .vjs-load-progress {
      direction: ltr;
    }
  }

  /* Fallback for environments where stylis-plugin-rtl is not present or insufficient.
     @noflip keeps the plugin from rewriting ltr back to rtl here. */
  [dir='rtl'] & {
    .video-js {
      /*! @noflip */
      .vjs-progress-holder,
      .vjs-play-progress,
      .vjs-load-progress {
        direction: ltr;
        unicode-bidi: isolate;
      }

      .vjs-time-tooltip {
        right: auto;
        left: 0;
      }
    }
  }
`;

export const VideoWrapper = styled.div<{ $aspectRatio?: AVideoAspectRatio; $visible?: boolean }>`
  position: relative;
  inline-size: 100%;
  aspect-ratio: ${({ $aspectRatio }) => ($aspectRatio ? getAspectRatioValue($aspectRatio) : '16 / 9')};
  overflow: hidden;
  border-radius: var(--t-images-border-radius, 14px);
  visibility: ${({ $visible = true }) => ($visible ? 'visible' : 'hidden')};
`;

export const VideoElement = styled.video`
  inline-size: 100%;
  block-size: 100%;
  object-fit: cover;
  display: block;
`;
